// Utility functions

/**
 * Get current date in IST (YYYY-MM-DD format)
 */
export function getCurrentISTDate(): string {
  const now = new Date()
  // Convert to IST (UTC+5:30)
  const istOffset = 5.5 * 60 * 60 * 1000
  const istTime = new Date(now.getTime() + istOffset)
  const utcDate = new Date(istTime.getTime() - (istTime.getTimezoneOffset() * 60 * 1000))
  return utcDate.toISOString().split('T')[0]
}

/**
 * Get next puzzle reset time (00:00 IST)
 */
export function getNextResetTime(): Date {
  const now = new Date()
  const istOffset = 5.5 * 60 * 60 * 1000
  const istTime = new Date(now.getTime() + istOffset)
  
  // Get today's date in IST
  const todayIST = new Date(istTime)
  todayIST.setHours(0, 0, 0, 0)
  
  // If already past midnight IST, next reset is tomorrow
  if (istTime >= todayIST) {
    todayIST.setDate(todayIST.getDate() + 1)
  }
  
  // Convert back to UTC
  return new Date(todayIST.getTime() - istOffset)
}

/**
 * Format time remaining until next reset (HH:MM:SS)
 */
export function formatTimeRemaining(targetDate: Date): string {
  const now = new Date()
  const diff = targetDate.getTime() - now.getTime()
  
  if (diff <= 0) return '00:00:00'
  
  const hours = Math.floor(diff / (1000 * 60 * 60))
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
  const seconds = Math.floor((diff % (1000 * 60)) / 1000)
  
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
}

/**
 * Generate share text for WhatsApp
 */
export function generateShareText(
  dayNumber: number,
  date: string,
  dailyWordResult?: { tries: number; grid: string },
  picRevealResult?: { solved: boolean; attempts: number },
  rankRushResult?: { solved: boolean }
): string {
  const lines = [
    `DailyGuess #${dayNumber} — ${date} 🇮🇳`,
    ''
  ]
  
  if (dailyWordResult) {
    lines.push(`Daily Word: ${dailyWordResult.tries}/6`)
    lines.push(dailyWordResult.grid)
  }
  
  if (picRevealResult) {
    if (picRevealResult.solved) {
      lines.push(`Pic Reveal: Solved in ${picRevealResult.attempts} 🔍`)
    } else {
      lines.push(`Pic Reveal: Not solved 🔍`)
    }
  }
  
  if (rankRushResult) {
    lines.push(`Rank Rush: ${rankRushResult.solved ? '✅' : '❌'}`)
  }
  
  lines.push('')
  lines.push('Can you beat me?')
  lines.push('https://dailyguess.in')
  
  return lines.join('\n')
}

/**
 * Copy text to clipboard
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch (e) {
    console.error('Failed to copy to clipboard:', e)
    return false
  }
}

/**
 * Open WhatsApp share link
 */
export function openWhatsAppShare(text: string): void {
  const encoded = encodeURIComponent(text)
  const url = `https://wa.me/?text=${encoded}`
  window.open(url, '_blank')
}

